import React, { useCallback, useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  TextField,
  Button,
  List,
  ListItemButton,
  ListItemText,
  IconButton,
  Stack,
  Alert,
  Chip,
  TablePagination,
} from '@mui/material';
import {
  Add,
  AttachFile as AttachFileIcon,
  Close as CloseIcon,
  Delete,
  Edit,
  Save,
} from '@mui/icons-material';
import { useQASources } from '@/hooks/useQASources';
import ConfirmDialog from '@/components/common/ConfirmDialog';
import AttachSourceToAgentsDialog from '@/components/admin/AttachSourceToAgentsDialog';
import adminApi from '@/services/admin.api';

interface AttachedAgent {
  _id: string;
  name: string;
  topics?: string[];
}

const QASource: React.FC = () => {
  const {
    items,
    loading,
    saving,
    error,
    success,
    page,
    setPage,
    total,
    PAGE_SIZE,
    selectedId,
    form,
    select,
    setTitle,
    setQuestion,
    addQuestion,
    removeQuestion,
    resetForm,
    save,
    remove,
    setAnswer,
  } = useQASources();

  const { title, questions, answer } = form;

  const [deleteId, setDeleteId] = useState<string | null>(null);
  const [openDeleteConfirm, setOpenDeleteConfirm] = useState(false);
  const [attachTarget, setAttachTarget] = useState<{ id: string; title: string } | null>(null);
  const [qaAgents, setQaAgents] = useState<Record<string, AttachedAgent[]>>({});
  const [loadingQaAgents, setLoadingQaAgents] = useState<Record<string, boolean>>({});
  const [detachConfirm, setDetachConfirm] = useState<{
    open: boolean;
    qaId: string | null;
    qaTitle: string;
    agentId: string | null;
    agentName: string;
  }>({ open: false, qaId: null, qaTitle: '', agentId: null, agentName: '' });

  const promptDelete = (id: string | null) => {
    if (id) {
      setDeleteId(id);
      setOpenDeleteConfirm(true);
    }
  };

  const handleConfirmDelete = async () => {
    if (deleteId) {
      await remove(deleteId);
      setOpenDeleteConfirm(false);
      setDeleteId(null);
    }
  };

  // Load attached agents for every item in the list (mirrors Files.tsx's pattern)
  // so each row can show who it's attached to without opening it.
  useEffect(() => {
    const loadAgentsForItems = async () => {
      for (const item of items) {
        if (!qaAgents[item._id] && !loadingQaAgents[item._id]) {
          setLoadingQaAgents((prev) => ({ ...prev, [item._id]: true }));
          try {
            const agents = await adminApi.getQASourceAgents(item._id);
            setQaAgents((prev) => ({ ...prev, [item._id]: agents }));
          } catch {
            setQaAgents((prev) => ({ ...prev, [item._id]: [] }));
          } finally {
            setLoadingQaAgents((prev) => ({ ...prev, [item._id]: false }));
          }
        }
      }
    };

    if (items.length > 0) {
      void loadAgentsForItems();
    }
  }, [items, qaAgents, loadingQaAgents]);

  const refreshAgentsFor = useCallback(async (qaId: string) => {
    try {
      const agents = await adminApi.getQASourceAgents(qaId);
      setQaAgents((prev) => ({ ...prev, [qaId]: agents }));
    } catch {
      // keep previous state on failure
    }
  }, []);

  const handleDetachClick = (qaId: string, qaTitle: string, agentId: string, agentName: string) => {
    setDetachConfirm({ open: true, qaId, qaTitle, agentId, agentName });
  };

  const handleConfirmDetach = useCallback(async () => {
    if (!detachConfirm.qaId || !detachConfirm.agentId) return;
    try {
      await adminApi.detachQASourceFromAgent(detachConfirm.agentId, detachConfirm.qaId);
      await refreshAgentsFor(detachConfirm.qaId);
      setDetachConfirm({ open: false, qaId: null, qaTitle: '', agentId: null, agentName: '' });
    } catch (err) {
      // Keep dialog open if detach fails; error surfaces through existing UI patterns in hooks.
    }
  }, [detachConfirm.qaId, detachConfirm.agentId, refreshAgentsFor]);

  return (
    <Box>
      <Typography variant="h4" fontWeight="bold" mb={3}>
        Q&A Sources
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert severity="success" sx={{ mb: 2 }}>
          {success}
        </Alert>
      )}

      <Grid container spacing={3}>
        {/* List */}
        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                <Typography variant="h6">Q&A List</Typography>
                <Button
                  size="small"
                  startIcon={<Add />}
                  onClick={resetForm}
                  disabled={saving || loading}
                >
                  New
                </Button>
              </Box>

              {loading ? (
                <Typography variant="body2">Loading...</Typography>
              ) : items.length === 0 ? (
                <Typography variant="body2" color="text.secondary">
                  No Q&A entries yet.
                </Typography>
              ) : (
                <List dense>
                  {items.map((item) => {
                    const agents = qaAgents[item._id] || [];
                    const isLoadingAgents = loadingQaAgents[item._id];

                    return (
                      <ListItemButton
                        key={item._id}
                        selected={item._id === selectedId}
                        onClick={() => select(item._id)}
                        sx={{ alignItems: 'flex-start' }}
                      >
                        <ListItemText
                          primary={item.title}
                          secondary={
                            <Box>
                              <Typography
                                component="span"
                                variant="caption"
                                color="text.secondary"
                                display="block"
                              >
                                {item.questions.length} question
                                {item.questions.length !== 1 ? 's' : ''} • Updated{' '}
                                {new Date(item.updatedAt).toLocaleDateString()}
                              </Typography>
                              {isLoadingAgents ? (
                                <Typography
                                  component="span"
                                  variant="caption"
                                  color="text.secondary"
                                  display="block"
                                  sx={{ mt: 0.5 }}
                                >
                                  Loading agents...
                                </Typography>
                              ) : agents.length > 0 ? (
                                <Box
                                  display="flex"
                                  alignItems="center"
                                  gap={0.5}
                                  flexWrap="wrap"
                                  sx={{ mt: 0.5 }}
                                >
                                  <Typography component="span" variant="caption" color="text.secondary">
                                    Attached to:
                                  </Typography>
                                  {agents.map((agent) => (
                                    <Chip
                                      key={agent._id}
                                      label={agent.name}
                                      size="small"
                                      onDelete={(e) => {
                                        e.stopPropagation();
                                        handleDetachClick(item._id, item.title, agent._id, agent.name);
                                      }}
                                      deleteIcon={<CloseIcon />}
                                      sx={{ height: 20, fontSize: '0.7rem' }}
                                    />
                                  ))}
                                </Box>
                              ) : (
                                <Typography
                                  component="span"
                                  variant="caption"
                                  color="text.secondary"
                                  display="block"
                                  sx={{ mt: 0.5 }}
                                >
                                  Not attached to any agents
                                </Typography>
                              )}
                            </Box>
                          }
                        />
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, ml: 1 }}>
                          <IconButton
                            size="small"
                            onClick={(e) => {
                              e.stopPropagation();
                              select(item._id);
                            }}
                            title="Edit"
                          >
                            <Edit fontSize="small" />
                          </IconButton>
                          <IconButton
                            size="small"
                            onClick={(e) => {
                              e.stopPropagation();
                              setAttachTarget({ id: item._id, title: item.title });
                            }}
                            title="Attach to agents"
                          >
                            <AttachFileIcon fontSize="small" />
                          </IconButton>
                          <IconButton
                            size="small"
                            color="error"
                            onClick={(e) => {
                              e.stopPropagation();
                              promptDelete(item._id);
                            }}
                            title="Delete"
                          >
                            <Delete fontSize="small" />
                          </IconButton>
                        </Box>
                      </ListItemButton>
                    );
                  })}
                </List>
              )}

              {total > 0 && (
                <TablePagination
                  component="div"
                  count={total}
                  page={page}
                  onPageChange={(_e, p) => setPage(p)}
                  rowsPerPage={PAGE_SIZE}
                  rowsPerPageOptions={[PAGE_SIZE]}
                />
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Form */}
        <Grid item xs={12} md={8}>
          <Card>
            <CardContent>
              <Typography variant="h6" mb={2}>
                {selectedId ? 'Edit Q&A' : 'Create Q&A'}
              </Typography>

              <TextField
                label="Title"
                fullWidth
                sx={{ mb: 2 }}
                value={title}
                onChange={(e) => setTitle(e.target.value)}
              />

              <Typography variant="subtitle2" gutterBottom>
                Questions
              </Typography>
              <Stack spacing={1} sx={{ mb: 2 }}>
                {questions.map((q, idx) => (
                  <Stack direction="row" spacing={1} key={idx}>
                    <TextField
                      fullWidth
                      size="small"
                      placeholder={`Question ${idx + 1}`}
                      value={q}
                      onChange={(e) => setQuestion(idx, e.target.value)}
                    />
                    <IconButton
                      color="error"
                      size="small"
                      onClick={() => removeQuestion(idx)}
                      disabled={questions.length === 1}
                    >
                      <Delete fontSize="small" />
                    </IconButton>
                  </Stack>
                ))}
                <Button variant="outlined" size="small" startIcon={<Add />} onClick={addQuestion}>
                  Add question
                </Button>
              </Stack>

              <TextField
                label="Answer"
                fullWidth
                multiline
                minRows={4}
                sx={{ mb: 2 }}
                value={answer}
                onChange={(e) => setAnswer(e.target.value)}
              />

              <Stack direction="row" spacing={2} justifyContent="flex-end">
                {selectedId && (
                  <Button
                    variant="outlined"
                    color="error"
                    startIcon={<Delete />}
                    onClick={() => promptDelete(selectedId)}
                    disabled={saving}
                  >
                    Delete
                  </Button>
                )}
                <Button variant="contained" startIcon={<Save />} onClick={save} disabled={saving}>
                  {saving ? 'Saving...' : 'Save'}
                </Button>
              </Stack>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <ConfirmDialog
        open={openDeleteConfirm}
        title="Confirm Delete"
        content="Are you sure you want to delete this Q&A source? This action cannot be undone."
        onConfirm={handleConfirmDelete}
        onCancel={() => setOpenDeleteConfirm(false)}
        loading={saving}
      />

      <AttachSourceToAgentsDialog
        open={!!attachTarget}
        onClose={() => setAttachTarget(null)}
        onSuccess={() => {
          if (!attachTarget) return;
          void refreshAgentsFor(attachTarget.id);
        }}
        sourceIds={attachTarget ? [attachTarget.id] : []}
        sourceLabel="Q&A"
        sourceName={attachTarget?.title || ''}
        getAttachedAgents={adminApi.getQASourceAgents}
        attachSourcesToAgents={adminApi.attachQASourcesToAgents}
        detachAgentFromSource={adminApi.detachQASourceFromAgent}
      />

      <ConfirmDialog
        open={detachConfirm.open}
        title="Detach Agent"
        content={`Are you sure you want to detach "${detachConfirm.agentName}" from "${detachConfirm.qaTitle}"?`}
        confirmText="Detach"
        cancelText="Cancel"
        onConfirm={() => void handleConfirmDetach()}
        onCancel={() =>
          setDetachConfirm({ open: false, qaId: null, qaTitle: '', agentId: null, agentName: '' })
        }
        loading={saving}
      />
    </Box>
  );
};

export default QASource;
